import { getDateTimeFormatter, getNumberFormatter } from "./intl";

interface BaseOptions {
  date: Date;
  locale: MatchiLocale;
  timeZone?: string;
}

interface FormatISOOptions extends Omit<BaseOptions, "locale"> {
  representation?: "datetime" | "date";
}

interface FormatDurationOptions extends BaseOptions {
  date2: Date;
}

export const formatISO = ({
  date, // TODO: Accept strings and number, convert to Date, return 'N/A' if invalid
  timeZone,
  representation = "datetime",
}: FormatISOOptions) => {
  const isDateTime = representation === "datetime";
  const timeValue = isDateTime ? "numeric" : undefined;

  const formatter = getDateTimeFormatter("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: timeValue,
    minute: timeValue,
    second: timeValue,
    timeZone,
  });

  return `${formatter.format(date).replace(" ", "T")}${isDateTime ? "Z" : ""}`;
};

export const formatHours = ({
  date, // TODO: Accept strings and number, convert to Date, return 'N/A' if invalid
  locale,
  timeZone,
}: BaseOptions) => {
  const formatter = getDateTimeFormatter(locale, {
    hour: "numeric",
    minute: "numeric",
    timeZone,
  });

  return formatter.format(date);
};

export const formatDuration = ({
  date, // TODO: Accept strings and number, convert to Date, return 'N/A' if invalid
  date2, // TODO: Accept strings and number, convert to Date, return 'N/A' if invalid
  locale,
}: FormatDurationOptions) => {
  const msDiff = Math.abs(date2.getTime() - date.getTime());
  const minutes = Math.round(msDiff / 60000);

  const formatter = getNumberFormatter(locale, {
    style: "unit",
    unit: "minute",
    unitDisplay: "long",
  });

  return formatter.format(minutes);
};

export const formatFullDate = ({ date, locale, timeZone }: BaseOptions) => {
  const formatter = getDateTimeFormatter(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  });

  return formatter.format(date);
};

export const formatDateSummary = ({ date, locale, timeZone }: BaseOptions) => {
  const formatter = getDateTimeFormatter(locale, {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone,
  });

  return formatter.format(date);
};

interface RelativeDayLabels {
  today: string;
  tomorrow: string;
  yesterday: string;
}

interface FormatRelativeDayOptions {
  date: Date;
  locale: MatchiLocale;
  labels: RelativeDayLabels;
}

export const formatRelativeDay = ({
  date,
  locale,
  labels,
}: FormatRelativeDayOptions) => {
  const normalized = new Date(date);
  const today = new Date();
  normalized.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((normalized.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return labels.today;
  if (diff === 1) return labels.tomorrow;
  if (diff === -1) return labels.yesterday;
  return formatDateSummary({ date, locale });
};

export const formatDateShort = ({ date, locale, timeZone }: BaseOptions) => {
  const formatter = getDateTimeFormatter(locale, {
    month: "long",
    year: "numeric",
    timeZone,
  });

  return formatter.format(date);
};
